/**
 * Shift-click range selection, as pure functions.
 *
 * A click sets one row; a shift-click sets every row between the last row
 * clicked (the anchor) and this one, in the order they're on screen right
 * now. The whole range takes the clicked box's NEW state, so shift-click
 * deselects a range as readily as it selects one. Rows that can't be
 * selected are skipped. An anchor that's no longer on screen (the page or
 * filter changed) makes the shift-click a plain click.
 */

/** Ids from `anchor` to `target` inclusive, in `visible` order; null if either isn't visible. */
export function rangeBetween<K>(visible: readonly K[], anchor: K | null | undefined, target: K): K[] | null {
  if (anchor === null || anchor === undefined) return null;
  const a = visible.indexOf(anchor);
  const b = visible.indexOf(target);
  if (a < 0 || b < 0) return null;
  return a <= b ? visible.slice(a, b + 1) : visible.slice(b, a + 1);
}

export interface RangeClick<K> {
  /** Ids in the order they're on screen (after sort, filter and paging). */
  visible: readonly K[];
  /** The last id clicked, or null for a plain (non-shift) click. */
  anchor: K | null;
  /** The id clicked. */
  target: K;
  isDisabled?: (id: K) => boolean;
}

/** The ids a click changes: the range for a shift-click, else just the target. Disabled ids dropped. */
export function rangeTargets<K>({ visible, anchor, target, isDisabled }: RangeClick<K>): K[] {
  if (isDisabled?.(target)) return [];
  const ids = rangeBetween(visible, anchor, target) ?? [target];
  return isDisabled ? ids.filter((id) => !isDisabled(id)) : ids;
}

/** The selection after a click: every id it changes is set to `checked`. */
export function applyRangeSelect<K>(
  input: RangeClick<K> & { selected: ReadonlySet<K>; checked: boolean },
): Set<K> {
  const next = new Set(input.selected);
  for (const id of rangeTargets(input)) {
    if (input.checked) next.add(id);
    else next.delete(id);
  }
  return next;
}

export type CheckState = 'none' | 'some' | 'all';

/** A header checkbox's state over `ids` (the selectable rows it covers). */
export function checkStateOf<K>(ids: readonly K[], isSelected: (id: K) => boolean): CheckState {
  if (ids.length === 0) return 'none';
  const n = ids.filter(isSelected).length;
  return n === 0 ? 'none' : n === ids.length ? 'all' : 'some';
}

/** `selected` with `ids` set to `checked`; the same Set back when nothing changes. */
export function setMany<K>(selected: ReadonlySet<K>, ids: readonly K[], checked: boolean): Set<K> {
  const next = new Set(selected);
  for (const id of ids) {
    if (checked) next.add(id);
    else next.delete(id);
  }
  return next;
}

/** `selected` narrowed to `keep`; the same Set back when nothing would be dropped. */
export function keepOnly<K, S extends ReadonlySet<K>>(selected: S, keep: readonly K[]): S | Set<K> {
  const allowed = new Set(keep);
  for (const id of selected) {
    if (!allowed.has(id)) return new Set([...selected].filter((x) => allowed.has(x)));
  }
  return selected;
}
